import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { TextInput } from '@interstate/components/TextInput';
import { Alert } from '@interstate/components/Alert';
import { Link } from '@interstate/components/Link';
import { Typography } from '@interstate/components/Typography';
import { CheckCircleIcon } from '@interstate/components/Icons';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import MessageWrapper from './components/message-wrapper/MessageWrapper';
import styles from './Finalize.module.scss';

const centerRow = { display: 'flex', justifyContent: 'center' };

function FinalizeByUsername() {
  const { tokens } = useInterstateTheme();
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [token, setToken] = useState(null);
  const [jti, setJti] = useState(null);
  const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  // Ref to manage WebOTP AbortController
  const abortControllerRef = useRef(null);

  // Fetch verification token on component mount
  useEffect(() => {
    fetch('/api/token')
      .then(res => {
        if (!res.ok) {
          throw new Error('Failed to fetch token');
        }
        return res.json();
      })
      .then(data => {
        setToken(data.token);
        setJti(data.jti);
        console.log('Verification token loaded');
      })
      .catch(err => {
        console.error('Failed to fetch verification token:', err);
        setStatus('error');
        setErrorMessage('Unable to load verification form');
      });
  }, []); // Empty dependency array = run once on mount

  // Set up WebOTP listener for automatic SMS OTP retrieval
  useEffect(() => {
    // Feature detection: Check if WebOTP API is available
    if ('OTPCredential' in window) {
      const otpInput = document.querySelector('input[autocomplete="one-time-code"]');

      if (!otpInput) return;

      // Create AbortController for this request
      const ac = new AbortController();
      abortControllerRef.current = ac;

      // Request OTP from SMS
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal
      })
        .then(otp => {
          // Auto-fill the OTP field when SMS is received
          setOtp(otp.code);
          console.log('WebOTP: OTP auto-filled from SMS');
        })
        .catch(err => {
          // Ignore abort errors (expected when user manually submits)
          if (err.name !== 'AbortError') {
            console.log('WebOTP error:', err);
          }
        });

      // Cleanup: abort the request when component unmounts
      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }
  }, []); // Run once on mount

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Abort WebOTP request if user manually submits
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Ensure token and jti are loaded
    if (!token || !jti) {
      setStatus('error');
      setErrorMessage('Unable to verify your account');
      return;
    }

    setStatus('loading');

    try {
      const response = await fetch('/api/sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Verification-Token': token
        },
        body: JSON.stringify({
          username: username.trim(),
          otp: otp.trim(),
          jti: jti
        })
      });

      if (response.status === 204 || response.ok) {
        setStatus('success');
      } else {
        setStatus('error');
        setErrorMessage('Unable to verify your account');
      }
    } catch (error) {
      console.error('Error calling finalize API:', error);
      setStatus('error');
      setErrorMessage('Unable to verify your account');
    }
  };

  const canSubmit = username.trim() !== '' && otp.length === 6;

  // Show loading state while token is being fetched
  if (status === 'form' && !token) {
    return (
      <Box className={styles.container}>
        <Box sx={centerRow}>
          <MessageWrapper>
            <Box sx={{ textAlign: 'center', padding: '48px 0' }}>
              <Typography variant="body-md" sx={{ marginBottom: '16px' }}>Loading...</Typography>
            </Box>
          </MessageWrapper>
        </Box>
      </Box>
    );
  }

  // FORM STATE
  if (status === 'form') {
    return (
      <Box className={styles.container}>
        <Box sx={centerRow}>
          <MessageWrapper>
            <Box sx={{ padding: '48px 16px' }}>
              <Typography variant="h2" sx={{ textAlign: 'center', marginBottom: '24px' }}>Verification Required</Typography>
              <Typography variant="body-md" sx={{ textAlign: 'center', marginBottom: '24px' }}>
                Please enter your username and one-time passcode (OTP) to verify your identity.
              </Typography>
              <form onSubmit={handleSubmit}>
                <Box sx={{ marginBottom: '16px' }}>
                  <TextInput
                    name="username"
                    type="text"
                    id="username"
                    label="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    required
                    autoFocus
                    autoComplete="username"
                  />
                </Box>
                <Box sx={{ marginBottom: '16px' }}>
                  <TextInput
                    name="otp"
                    type="tel"
                    id="otp"
                    label="OTP Code"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (value.length <= 6) {
                        setOtp(value);
                      }
                    }}
                    maxLength={6}
                    masking={{ mask: [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/], guide: false }}
                    placeholder="Enter 6-digit code"
                    autoComplete="one-time-code"
                    required
                  />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Button type="submit" buttonStyle="primary" disabled={!canSubmit}>
                    Verify
                  </Button>
                </Box>
              </form>
            </Box>
          </MessageWrapper>
        </Box>
      </Box>
    );
  }

  // LOADING STATE
  if (status === 'loading') {
    return (
      <Box className={styles.container}>
        <Box sx={centerRow}>
          <MessageWrapper>
            <Box sx={{ textAlign: 'center', padding: '48px 0' }}>
              <Typography variant="body-md" sx={{ marginBottom: '16px' }}>Validating Your Information...</Typography>
            </Box>
          </MessageWrapper>
        </Box>
      </Box>
    );
  }

  // SUCCESS STATE
  if (status === 'success') {
    return (
      <Box className={styles.container}>
        <Box sx={centerRow}>
          <MessageWrapper>
            <div className={styles.doneContainer}>
              <div className={styles.doneContentWrapper}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <CheckCircleIcon style={{ fontSize: 60, color: tokens.SemColorOnSurfaceSuccessDefault }} />
                  <Typography variant="h1" sx={{ color: tokens.SemColorOnSurfaceSuccessDefault, marginTop: '12px', marginBottom: '24px' }}>Verified!</Typography>
                </Box>
                <Typography variant="body-md" sx={{ textAlign: 'center' }}>
                  Thank you for verifying your contact information.
                </Typography>
              </div>
              <div>
                <Typography variant="body-md" sx={{ textAlign: 'center' }}>
                  You may close this window or continue to&nbsp;
                  <Link href="https://www.manheim.com">Manheim.com.</Link>
                </Typography>
              </div>
            </div>
          </MessageWrapper>
        </Box>
      </Box>
    );
  }

  // ERROR STATE
  return (
    <Box className={styles.container}>
      <Box sx={centerRow}>
        <MessageWrapper>
          <Box sx={{ padding: '48px 16px' }}>
            <Alert type="error" role="alert">
              {errorMessage || 'Unable to verify your account'}.
              If you need additional assistance, please call
              Manheim Client Care at <b>1-866-626-4346</b>.
            </Alert>
          </Box>
        </MessageWrapper>
      </Box>
    </Box>
  );
}

export default FinalizeByUsername;
